import React, { useState, useEffect } from 'react';
import { downloadAuthenticatedFile } from '../../utils/downloadHelper';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Collapse,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    AttachFile,
    ExpandLess,
    ExpandMore,
    Description,
    GetApp,
} from '@material-ui/icons';
import Swal from 'sweetalert2';
import { Modal, PrimaryButton, SecondaryButton } from '../../ui';
import { ClientQuoteDetailsApi } from '../../../client/clientQuoteDetails/service/clientQuoteDetailsApi';

const useStyles = makeStyles((theme) => ({
    sectionCard: {
        border: `1px solid #E2E8F0`,
        borderRadius: 12,
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
    },
    sectionHeader: {
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    sectionHeaderExpanded: {
        marginBottom: theme.spacing(1.75),
    },
    sectionTitle: {
        margin: 0,
        fontSize: 16,
        fontWeight: 700,
        color: '#0E4A64',
        lineHeight: 1.3,
    },
    sectionBody: {
        padding: 0,
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.2, 1.5),
        marginBottom: theme.spacing(1),
        backgroundColor: '#F8FAFC',
        borderRadius: 10,
        border: '1px solid #E2E8F0',
    },
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    fileIcon: {
        color: '#64748B',
    },
    fileName: {
        fontWeight: 500,
        color: '#1E293B',
    },
    uploadDate: {
        color: '#64748B',
    },
    emptyMessage: {
        color: '#64748B',
        fontStyle: 'italic',
        textAlign: 'center',
        padding: theme.spacing(2),
    },
    downloadButton: {
        color: '#00C2E0',
        '&:hover': {
            backgroundColor: 'rgba(0, 194, 224, 0.04)',
        },
    },
    uploadButton: {
        backgroundColor: '#00C2E0',
        color: '#FFFFFF',
        borderRadius: 999,
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#00acc1',
        },
    },
    inputFile: {
        display: 'none',
    },
    fileLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        cursor: 'pointer',
        padding: theme.spacing(1),
        border: '1px dashed #94a3b8',
        borderRadius: 8,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        '&:hover': {
            borderColor: '#00C2E0',
            backgroundColor: '#f0fdfa',
        },
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
}));

const NotasFileSection = ({ serviceFiles, currentLanguage, quoteId, role }) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(true);
    const [notaFiles, setNotaFiles] = useState([]);

    // Upload modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [nfNumberInput, setNfNumberInput] = useState('');
    const [uploading, setUploading] = useState(false);

    const isAdminOrWorkshop = role === 'admin' || role === 'workshop';

    useEffect(() => {
        if (Array.isArray(serviceFiles)) {
            const filtered = serviceFiles.filter(f => f.type === 'notas');
            setNotaFiles(filtered);
        }
    }, [serviceFiles]);

    const toggleSection = () => {
        setExpanded((prev) => !prev);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const handleDownload = async (file) => {
        const url = file.downloadUrl || file.download_url;
        const displayName = getDisplayName(file);

        if (url) {
            try {
                await downloadAuthenticatedFile(url, displayName);
            } catch (error) {
                console.error('Falha ao baixar arquivo:', error);
                Swal.fire('Erro', 'Ocorreu um erro ao tentar baixar o arquivo.', 'error');
            }
        } else {
            Swal.fire('Info', `O arquivo ${displayName} não possui link de download disponível.`, 'info');
        }
    };

    const handleOpenModal = () => {
        setSelectedFile(null);
        setNfNumberInput('');
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        if (uploading) return;
        setModalOpen(false);
        setSelectedFile(null);
        setNfNumberInput('');
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            Swal.fire('Atenção', 'Selecione um arquivo para continuar.', 'warning');
            return;
        }

        if (!nfNumberInput.trim()) {
            Swal.fire('Atenção', 'Informe o Número da Nota.', 'warning');
            return;
        }

        setUploading(true);

        try {
            await ClientQuoteDetailsApi.uploadNfFile(quoteId, selectedFile, nfNumberInput, 'notas');

            handleCloseModal();

            Swal.fire(
                'Sucesso',
                `Nota Fiscal '${nfNumberInput.trim()}' anexada com sucesso.`,
                'success'
            ).then(() => {
                window.location.reload();
            });

        } catch (error) {
            console.error('Erro ao enviar a nota fiscal', error);
            Swal.fire('Erro', 'Não foi possível anexar o arquivo. Tente novamente.', 'error');
        } finally {
            setUploading(false);
        }
    };

    const getDisplayName = (file) => {
        return file.fileName || file.file_name || file.budget_file_name || 'Nota Fiscal';
    };

    return (
        <>
            <Card className={classes.sectionCard}>
                <Box
                    className={`${classes.sectionHeader} ${expanded ? classes.sectionHeaderExpanded : ''}`}
                    onClick={toggleSection}
                    role="button"
                    tabIndex={0}
                >
                    <Typography className={classes.sectionTitle}>
                        📌 NOTAS FISCAIS
                    </Typography>
                    <IconButton size="small">
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>
                <Collapse in={expanded}>
                    <Box className={classes.sectionBody}>
                        {isAdminOrWorkshop && (
                            <Button
                                variant="contained"
                                className={classes.uploadButton}
                                startIcon={<AttachFile />}
                                onClick={handleOpenModal}
                                style={{ marginBottom: 16 }}
                            >
                                Anexar Nota Fiscal
                            </Button>
                        )}

                        {notaFiles.length === 0 ? (
                            <Typography className={classes.emptyMessage}>
                                Nenhuma nota fiscal encontrada.
                            </Typography>
                        ) : (
                            notaFiles.map((file) => (
                                <Box key={file.id} className={classes.fileItem}>
                                    <Box className={classes.fileInfo}>
                                        <Description className={classes.fileIcon} />
                                        <Box>
                                            <Typography className={classes.fileName} variant="body1">
                                                {getDisplayName(file)}
                                            </Typography>
                                            <Typography className={classes.uploadDate} variant="caption">
                                                Enviado em: {formatDate(file.createdAt || file.data_upload)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        className={classes.downloadButton}
                                        size="small"
                                        title="Baixar Nota Fiscal"
                                        onClick={() => handleDownload(file)}
                                    >
                                        <GetApp />
                                    </IconButton>
                                </Box>
                            ))
                        )}
                    </Box>
                </Collapse>
            </Card>

            <Modal
                isOpen={modalOpen}
                onClose={handleCloseModal}
                title="Anexar Nota Fiscal"
            >
                <Box p={2}>
                    <TextField
                        label="Número da Nota"
                        value={nfNumberInput}
                        onChange={(e) => setNfNumberInput(e.target.value)}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        placeholder="Ex: NF-1029"
                    />

                    <input
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                        className={classes.inputFile}
                        id="notas-file-upload-input"
                        type="file"
                        onChange={handleFileSelect}
                    />
                    <label htmlFor="notas-file-upload-input" className={classes.fileLabel}>
                        <AttachFile style={{ color: '#64748B' }} />
                        <Typography variant="body2" style={{ color: '#64748B' }}>
                            {selectedFile ? selectedFile.name : 'Selecionar arquivo...'}
                        </Typography>
                    </label>

                    <Box className={classes.modalActions}>
                        <SecondaryButton onClick={handleCloseModal} disabled={uploading}>
                            Cancelar
                        </SecondaryButton>
                        <PrimaryButton onClick={handleUpload} disabled={uploading}>
                            {uploading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Enviar'}
                        </PrimaryButton>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

NotasFileSection.propTypes = {
    serviceFiles: PropTypes.array,
    currentLanguage: PropTypes.object,
    quoteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role: PropTypes.string,
};

NotasFileSection.defaultProps = {
    serviceFiles: [],
    currentLanguage: {},
    quoteId: null,
    role: '',
};

export default NotasFileSection;
